import React from 'react';
import VerifiedFlame from './VerifiedFlame';

// SMTQ overall raw score is out of 56 (14 items × 4).
export const SMTQ_GLOBAL_MAX = 56;
export const SMTQ_CREDIT = 'SMTQ — Sheard, Golby & van Wersch (2009)';

/**
 * Parse an SMTQ timestamp. `/api/smtq` payloads end in "Z", but the profile's
 * `smtq_completed_at` and older stored summaries are Python `isoformat()` with
 * no offset — those are UTC too, so an offset-less string is read as UTC
 * (a bare `new Date()` would read it as local time). Returns null if unparseable.
 */
export function parseSmtqDate(value) {
  if (!value || typeof value !== 'string') return null;
  const s = value.trim();
  const hasOffset = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  // JS Date only keeps milliseconds; trim microseconds so parsing is reliable.
  const iso = s.replace(/(\.\d{3})\d+/, '$1');
  const d = new Date(hasOffset ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const formatDate = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Bar length comes from the band in four equal steps, so it always agrees with
// the label. `percent` is percent-of-range and can't be compared across
// subscales (spec §6.2), so it is never drawn. Unknown band → empty bar.
const SMTQ_BAND_BAR_PERCENT = { developing: 25, emerging: 50, strong: 75, exceptional: 100 };

export function smtqBarPercent(band) {
  return Object.prototype.hasOwnProperty.call(SMTQ_BAND_BAR_PERCENT, band) ? SMTQ_BAND_BAR_PERCENT[band] : 0;
}

function SubscaleBar({ sub }) {
  const pct = smtqBarPercent(sub.band);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span style={{ color: '#0f172a', fontWeight: 600 }}>{sub.name}</span>
        <span style={{ color: '#64748b', fontWeight: 600, fontSize: 12 }}>{sub.band_label}</span>
      </div>
      <div
        role="progressbar"
        aria-label={`${sub.name}, ${sub.band_label}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        style={{ height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: '#4D8064', borderRadius: 999 }} />
      </div>
    </div>
  );
}

const yesNo = (v) => (v ? 'Yes' : 'No');

function QualitySection({ quality }) {
  const rows = [
    ['Straight-lining', yesNo(quality.straight_lining)],
    ['Inconsistency', quality.inconsistency ?? '—'],
    ['Ceiling', yesNo(quality.ceiling)],
    ['Fast (under 30 s)', yesNo(quality.fast)],
  ];
  return (
    <section aria-label="Response quality" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Response quality (staff only)</div>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 16, rowGap: 4, margin: 0, fontSize: 13 }}>
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt style={{ color: '#64748b' }}>{label}</dt>
            <dd style={{ margin: 0, color: '#0f172a', fontWeight: 600 }}>{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

/**
 * Read-only SMTQ (Sports Mental Toughness Questionnaire) summary — the web twin
 * of iOS `MentalToughnessSummaryCard` / the Android coach card. Built only from
 * the Summary shape, which carries no feedback, next steps or responses.
 *
 * Props:
 *   summary   — `profile.smtq_summary` (or null → renders `emptyText`, or nothing).
 *   quality   — optional quality flags from GET /api/smtq/users/{id}/latest.
 *               Callers pass it ONLY for admin / internal_staff viewers.
 *   testId    — same ids as the apps (docs/accessibility-conventions.md).
 *   emptyText — shown under the title when the test hasn't been taken; omit to
 *               render nothing (coach-facing views).
 */
export default function MentalToughnessCard({ summary, quality, testId = 'profile_mental_toughness_card', emptyText }) {
  if (!summary || !summary.global) {
    if (!emptyText) return null;
    return (
      <div className="form-section" data-testid={testId}>
        <h2 className="form-section__title" style={{ margin: '0 0 8px' }}>Mental Toughness</h2>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>{emptyText}</div>
      </div>
    );
  }
  const completed = parseSmtqDate(summary.completed_at);
  const subscales = Array.isArray(summary.subscales) ? summary.subscales : [];
  const g = summary.global;

  return (
    <div className="form-section" data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <h2 className="form-section__title" style={{ margin: 0 }}>Mental Toughness</h2>
        {summary.verified && (
          // A clean self-report: consistent answers and taken over ≥30 s
          // (computed by the scoring service). NOT staff/partner verification.
          <VerifiedFlame size={14} label="Verified self-report" title="Verified self-report: consistent answers, not rushed" />
        )}
        <span style={{ flex: 1 }} />
        {completed && (
          <span style={{ fontSize: 12, color: '#64748b' }}>Completed {formatDate(completed)}</span>
        )}
      </div>

      <div
        role="group"
        aria-label={`Overall ${g.raw} out of ${SMTQ_GLOBAL_MAX}, ${g.band_label}`}
        style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}
      >
        <span style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{g.raw}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>/ {SMTQ_GLOBAL_MAX}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#4D8064', background: 'rgba(77, 128, 100, 0.12)', borderRadius: 999, padding: '4px 10px' }}>
          {g.band_label}
        </span>
      </div>

      {subscales.map((sub) => <SubscaleBar key={sub.key} sub={sub} />)}

      {quality && <QualitySection quality={quality} />}

      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{SMTQ_CREDIT}</div>
    </div>
  );
}
